import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { EstateAssets, EstateProfile, IHTCalculation, ActionPack, ConsistencyFinding } from '../../types';
import {
    ReportMasthead,
    VerdictTrio,
    InterpretationLine,
    SectionHeader,
    LedgerRow,
} from '../ui';
import { getProbateStatusExplanation } from './probateLogic';
import { ConsistencyConflictPanel, ConsistencyReviewNudges } from './ConsistencyConflictPanel';
import { resolveResidenceForConsumers } from '../../server/services/rnrbResidenceFacts';
import { selectReportPanels } from './selectReportPanels';
import { TRUST_ESTATE_PROVISIONAL, TRUST_ESTATE_PROVISIONAL_SHORT, TRUST_ESTATE_ZERO_TAX } from './trustEstateProvisional';

export interface ReportBodyProps {
    profile: EstateProfile;
    assets: EstateAssets;
    ihtCalc: IHTCalculation;
    actionPack: ActionPack;
    probateConfidence?: 'definite' | 'likely' | 'uncertain' | null;
    /** Print path = no on-screen affordances, narrower measure. */
    printMode?: boolean;
    /**
     * Cross-field consistency findings (already computed by the validation pass).
     * Undefined ⇒ the check wasn't run for this caller (no consistency UI).
     * An array (even empty) ⇒ render the conflict panel / review nudges / clean
     * confirmation, and gate the confident headline when any finding is blocking.
     */
    consistencyFindings?: ConsistencyFinding[];
    /** Jump the wizard to a step id; threaded into the conflict jump-links. */
    goToStep?: (stepId: string) => boolean;
}

const fmtGBP = (n: number | null | undefined) =>
    (n ?? 0).toLocaleString('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0,
    });

const fmtDate = (s?: string | null) => {
    if (!s) return '—';
    try {
        return new Date(s).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'long', year: 'numeric',
        });
    } catch { return s; }
};

const fmtPreparedAt = (d: Date) =>
    d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const ROUTE_LABELS: Record<string, string> = {
    POST_2022_EXCEPTED: 'Excepted estate',
    POST_2022_IHT400: 'Full IHT400',
    PRE_2022_LEGACY: 'Legacy (pre-2022)',
    NEEDS_MORE_INFO: 'Route pending',
};

const ROUTE_SUBTEXT: Record<string, string> = {
    POST_2022_EXCEPTED: 'Simplified return — values reported on the probate application.',
    POST_2022_IHT400: 'Full IHT400 return required before probate is granted.',
    PRE_2022_LEGACY: 'Pre-2022 rules apply; an IHT205 or IHT400 will be needed.',
    NEEDS_MORE_INFO: 'Filing route depends on outstanding information below.',
};

const maritalLabel = (v?: string) => ({
    MARRIED_OR_CP: 'married / civil partnership',
    WIDOWED: 'widowed',
    DIVORCED_OR_SEPARATED: 'divorced / separated',
    NEVER_MARRIED: 'never married',
} as Record<string, string>)[v ?? ''] ?? null;

const PENDING_LABELS: Record<string, string> = {
    MISSING_NET_VALUE: 'Confirm net estate value (assets less debts).',
    MISSING_CLOSELY_INHERITED_RESIDENCE_VALUE: 'Confirm how the main residence passes to direct descendants.',
    MISSING_UNUSED_NRB_PERCENT: 'Confirm unused Nil Rate Band transferred from a predeceased spouse.',
    MISSING_UNUSED_RNRB_PERCENT: 'Confirm unused Residence Nil Rate Band transferred from a predeceased spouse.',
    MISSING_NET_FOR_TAPER: 'Confirm net estate value used for the £2m RNRB taper.',
};

const pendingLabel = (key: string) => PENDING_LABELS[key] ?? key.replace(/_/g, ' ').toLowerCase();

const addMonths = (s?: string, months = 0): string | null => {
    if (!s) return null;
    try {
        const d = new Date(s);
        d.setMonth(d.getMonth() + months);
        return d.toISOString().slice(0, 10);
    } catch { return null; }
};

const endOfMonth = (s?: string): string | null => {
    if (!s) return null;
    try {
        const d = new Date(s);
        const eom = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        return eom.toISOString().slice(0, 10);
    } catch { return null; }
};

const formatYears = (y: number): string => {
    const whole = Math.floor(y);
    const months = Math.round((y - whole) * 12);
    if (months === 0) return `${whole}y`;
    return `${whole}y ${months}m`;
};

interface AssetRow { id: string; label: string; value: number; note?: string; }

const buildEstateLedger = (assets: EstateAssets): AssetRow[] => {
    const rows: AssetRow[] = [];

    (assets.properties ?? []).forEach((p, i) => {
        const fullVal = Number(p.estimated_value) || 0;
        const sharePct = p.deceased_share_percent ?? 100;
        const mortgage = p.has_mortgage ? (Number(p.mortgage_balance) || 0) : 0;
        const netEquity = Math.max(0, fullVal - mortgage);
        const estateShare = Math.round(netEquity * sharePct / 100);
        const ownershipNote = p.ownership_type === 'joint_tenants'
            ? 'Joint tenants — passes by survivorship'
            : p.ownership_type === 'tenants_in_common'
            ? `Tenants in common (${sharePct}%)`
            : sharePct < 100 ? `${sharePct}% share` : undefined;
        rows.push({
            id: `prop-${p.id ?? i}`,
            label: p.label || p.address?.split(',')[0] || `Property ${i + 1}`,
            value: estateShare,
            note: ownershipNote,
        });
    });

    (assets.bank_accounts ?? []).forEach((b, i) => {
        rows.push({
            id: `bank-${b.id ?? i}`,
            label: b.label || b.provider || `Bank account ${i + 1}`,
            value: Number(b.estimated_value) || 0,
            note: b.ownership === 'joint' ? 'Joint account' : undefined,
        });
    });

    (assets.investments ?? []).forEach((inv, i) => {
        rows.push({
            id: `inv-${inv.id ?? i}`,
            label: inv.label || inv.provider || `Investment ${i + 1}`,
            value: Number(inv.estimated_value) || 0,
            note: inv.ownership === 'joint' ? 'Joint holding' : undefined,
        });
    });

    (assets.pensions ?? []).filter(p => p.in_estate !== false).forEach((p, i) => {
        rows.push({
            id: `pen-${p.id ?? i}`,
            label: p.label || p.provider || `Pension ${i + 1}`,
            value: Number(p.estimated_value) || 0,
            note: p.in_estate === true ? 'In estate' : 'Pension',
        });
    });

    if (assets.life_insurance?.exists && assets.life_insurance.written_in_trust === false) {
        rows.push({
            id: 'life-insurance',
            label: 'Life insurance (not in trust)',
            value: Number(assets.life_insurance.estimated_value) || 0,
        });
    }

    if (assets.foreign_assets?.exists) {
        const v = assets.foreign_assets.value_estimate ?? assets.foreign_assets.value ?? 0;
        if (v > 0) {
            rows.push({
                id: 'foreign-assets',
                label: 'Assets outside the UK',
                value: Number(v) || 0,
                note: (assets.foreign_assets.countries ?? []).join(', ') || undefined,
            });
        }
    }

    return rows;
};

interface RedFlag { id: string; title: string; detail: string; }

const detectRedFlags = (
    profile: EstateProfile,
    assets: EstateAssets,
    ihtCalc: IHTCalculation,
): RedFlag[] => {
    const flags: RedFlag[] = [];
    const b = ihtCalc.breakdown ?? ({} as IHTCalculation['breakdown']);
    const iht = profile.iht_details ?? {};
    const ts = (iht as any)?.trust_screening ?? {};

    if (ts.has_gift_with_reservation === true) {
        const v = ts.gwr_value_estimate;
        flags.push({
            id: 'gwr',
            title: 'Gift with reservation of benefit',
            detail: `Assets given away during the deceased's lifetime were still being used or enjoyed at death${v ? ` (estimated value ${fmtGBP(v)})` : ''}. Such gifts are treated as part of the estate for IHT (IHTA 1984 s102).`,
        });
    }

    if (ts.created_or_transferred_trust === true || ts.benefited_from_trust === true) {
        flags.push({
            id: 'trust',
            title: 'Trust involvement',
            detail: 'Trust or settlement arrangements have been identified. Trust property may need to be aggregated with the estate; specialist advice is recommended before filing.',
        });
    }

    if (assets.foreign_assets?.exists === true) {
        const countries = (assets.foreign_assets.countries ?? []).join(', ');
        flags.push({
            id: 'foreign',
            title: 'Foreign assets',
            detail: `Assets located outside the UK${countries ? ` (${countries})` : ''} may require separate probate or succession procedures in the relevant jurisdiction. Foreign inheritance taxes may also apply.`,
        });
    }

    const taperBase = b.rnrb_taper_base ?? b.net_value ?? b.gross_value ?? 0;
    if (taperBase > 2_000_000) {
        flags.push({
            id: 'rnrb-taper',
            title: 'Estate above £2m RNRB taper threshold',
            detail: `Net estate of ${fmtGBP(taperBase)} exceeds the £2m threshold; the Residence Nil Rate Band tapers by £1 for every £2 above £2m and may be lost in full.`,
        });
    }

    const pensionInEstate = (assets.pensions ?? []).some(p => p.in_estate === true);
    if (pensionInEstate) {
        flags.push({
            id: 'pension-in-estate',
            title: 'Pension included in the estate',
            detail: 'A pension benefit forms part of the estate for IHT purposes. Most discretionary pension lump sums are outside the estate; confirm scheme rules and the trustee\'s discretion before filing.',
        });
    }

    if ((b.gifts_chargeable_total ?? 0) > 0) {
        flags.push({
            id: 'chargeable-gifts',
            title: 'Chargeable lifetime gifts within 7 years',
            detail: 'Gifts made within 7 years before death reduce the Nil Rate Band available to the estate and may carry tax in their own right. See Section III for taper detail.',
        });
    }

    return flags;
};

interface Optimisation { id: string; title: string; detail: string; }

const detectOptimisations = (
    profile: EstateProfile,
    assets: EstateAssets,
    ihtCalc: IHTCalculation,
): Optimisation[] => {
    const opts: Optimisation[] = [];
    const b = ihtCalc.breakdown ?? ({} as IHTCalculation['breakdown']);
    const taxDue = ihtCalc.total_tax_due ?? 0;
    if (taxDue <= 0) return [];

    const rate = b.iht_rate_applied ?? b.tax_rate ?? 0.4;

    const chargeableGifts = b.gifts_chargeable_total ?? 0;
    const possibleIncomeExemption = (assets as any)?.possible_income_gift_exemption;
    if (chargeableGifts > 0 && possibleIncomeExemption !== false) {
        opts.push({
            id: 'income-gift-exemption',
            title: 'Normal expenditure out of income exemption',
            detail: `Chargeable gifts of ${fmtGBP(chargeableGifts)} may qualify as "normal expenditure out of income" (IHTA 1984 s21) where the gifts were habitual, made from surplus income and did not reduce the donor's standard of living. Gathering evidence of the income/gift pattern could remove these gifts from the IHT calculation.`,
        });
    }

    const rnrbAvailable = b.rnrb_available ?? 0;
    const rnrbBeforeTaper = b.rnrb_before_taper ?? 0;
    if (rnrbAvailable === 0 && rnrbBeforeTaper === 0) {
        // Only a resolved (or legacy provisional) QRI from the shared Stage-1 resolution — never is_main_residence,
        // and never a property while the nomination or the residence history is open.
        const mainHome = resolveResidenceForConsumers(assets.properties, profile.iht_details).qri;
        const hasDescendants =
            (profile.family?.children_over_18 ?? 0) > 0
            || (profile.family?.children_under_18 ?? 0) > 0;
        if (mainHome && hasDescendants) {
            const cap = Math.min(175_000, Number(mainHome.estimated_value) || 0);
            opts.push({
                id: 'rnrb-route',
                title: 'Route the residence to direct descendants',
                detail: `A main residence is in the estate and the deceased has direct descendants, but the Residence Nil Rate Band is not currently being claimed. Up to ${fmtGBP(cap)} of allowance — roughly ${fmtGBP(Math.round(cap * rate))} of tax — may be recoverable if beneficiary allocations qualify.`,
            });
        }
    }

    const charity = b.charity_exemption_total ?? 0;
    const taxableValue = b.taxable_value ?? 0;
    if (!b.charitable_rate_applies && charity === 0 && taxableValue > 0) {
        opts.push({
            id: 'charitable-rate',
            title: '36% reduced rate on charitable bequests',
            detail: 'Where 10% or more of the baseline taxable estate is left to charity, the remaining estate is taxed at 36% rather than 40% (IHTA 1984 Sch 1A). For larger estates this can produce a meaningful saving and benefit the charitable beneficiaries directly.',
        });
    }

    return opts;
};

export const ReportBody: React.FC<ReportBodyProps> = ({
    profile, assets, ihtCalc, actionPack, printMode = false, consistencyFindings, goToStep,
}) => {
    // Cross-field consistency presentation. Undefined ⇒ check not run for this
    // caller. Blocking findings gate the confident headline (see below).
    const consistencyEnabled = Array.isArray(consistencyFindings);
    const blockingFindings = consistencyEnabled ? consistencyFindings!.filter((f) => f.severity === 'blocking') : [];
    const reviewFindings = consistencyEnabled ? consistencyFindings!.filter((f) => f.severity === 'review') : [];
    const consistencyBlocked = blockingFindings.length > 0;
    const d = profile.deceased || {};
    const fam = profile.family || {};
    const breakdown = ihtCalc.breakdown ?? ({} as IHTCalculation['breakdown']);

    // ── Masthead ─────────────────────────────────────────────
    const now = new Date();
    const reportRef = profile.case_label
        || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-DRAFT`;
    const preparedFor = fam.executor_name
        ? `${fam.executor_name} (executor)`
        : 'Executor';
    const preparedAtText = fmtPreparedAt(now);

    // ── Title block ──────────────────────────────────────────
    const fullName = [d.firstName, d.lastName].filter(Boolean).join(' ').trim();
    const deceasedName = fullName || 'Estate of unnamed deceased';
    const dodLabel = fmtDate(d.date_of_death);
    const domicileLabel =
        d.domicile === 'UK' ? 'UK domiciled'
        : d.domicile === 'NON_UK' ? 'Non-UK domiciled'
        : 'Domicile not confirmed';
    const grossEstate = breakdown.gross_value ?? 0;

    // ── Verdict trio ─────────────────────────────────────────
    const pendingItems = ihtCalc.blockers ?? [];
    const probateInfo = getProbateStatusExplanation({ assets, pendingItems });

    const filingForm = consistencyBlocked
        ? 'Provisional'
        : (ROUTE_LABELS[actionPack.recommended_pathway] ?? 'Route pending');
    const filingSubtext = (() => {
        if (consistencyBlocked) {
            return 'Unresolved input conflicts above — confirm the correct answers before relying on a filing route.';
        }
        if (actionPack.recommended_pathway === 'POST_2022_IHT400' && d.date_of_death) {
            const due = addMonths(d.date_of_death, 12);
            return due ? `IHT400 due by ${fmtDate(due)}.` : ROUTE_SUBTEXT[actionPack.recommended_pathway];
        }
        return ROUTE_SUBTEXT[actionPack.recommended_pathway] ?? 'Filing route to be confirmed.';
    })();

    const ihtDue = ihtCalc.total_tax_due ?? 0;
    // WP1: trust property that may belong in the estate is not included (engine gap) — every tax statement is
    // provisional. Wording only; the figure shown is the engine's, unchanged.
    const trustEstateProvisional = !!breakdown.trust_estate_inclusion_gap;
    const ihtSubtext = (() => {
        if (ihtDue <= 0) return trustEstateProvisional ? TRUST_ESTATE_PROVISIONAL_SHORT : 'No inheritance tax expected on this estate.';
        const eom = endOfMonth(d.date_of_death);
        const due = eom ? addMonths(eom, 6) : null;
        const payable = due
            ? `Payable by ${fmtDate(due)}, before probate is granted.`
            : 'Payable within 6 months of the end of the month of death, before probate is granted.';
        return trustEstateProvisional ? `${TRUST_ESTATE_PROVISIONAL_SHORT} ${payable}` : payable;
    })();

    // ── Interpretation line ──────────────────────────────────
    const interpretation = trustEstateProvisional
        ? (ihtDue > 0
            ? `On the information currently entered, net estate of ${fmtGBP(breakdown.net_value)} exceeds the available allowances by ${fmtGBP(breakdown.taxable_value)}, giving tax of ${fmtGBP(ihtDue)}. ${TRUST_ESTATE_PROVISIONAL}`
            : `${TRUST_ESTATE_ZERO_TAX} ${TRUST_ESTATE_PROVISIONAL}`)
        : ihtDue > 0
        ? `Net estate of ${fmtGBP(breakdown.net_value)} exceeds the available allowances by ${fmtGBP(breakdown.taxable_value)}; tax of ${fmtGBP(ihtDue)} is payable before probate is granted.`
        : `Net estate of ${fmtGBP(breakdown.net_value)} sits within the available allowances. No inheritance tax is expected on the figures provided.`;

    // ── Section II: estate position ──────────────────────────
    const ledger = buildEstateLedger(assets);
    const debts = breakdown.debts_total ?? 0;
    const netEstate = breakdown.net_value ?? Math.max(0, grossEstate - debts);

    // ── Section III: IHT calculation ─────────────────────────
    const nrb = breakdown.nrb_available ?? 0;
    const rnrb = breakdown.rnrb_available ?? 0;
    const totalAllowance = breakdown.total_allowance ?? (nrb + rnrb);
    const taxableValue = breakdown.taxable_value ?? 0;
    const ihtRate = breakdown.iht_rate_applied ?? breakdown.tax_rate ?? 0.4;
    const spouseExemption = breakdown.spouse_exemption_total ?? 0;
    const charityExemption = breakdown.charity_exemption_total ?? 0;
    const bprRelief = breakdown.bpr_relief_total ?? 0;
    const aprRelief = breakdown.apr_relief_total ?? 0;
    const giftsChargeable = breakdown.gifts_chargeable_total ?? 0;
    const giftTaperDetails = breakdown.gift_taper_details ?? [];
    const taperReliefTotal = breakdown.taper_relief_total ?? 0;
    const downsizing = breakdown.downsizing_addition ?? 0;
    const tnrbConfidence = breakdown.tnrb_confidence;
    const tnrbNrbPct = breakdown.tnrb_nrb_percent ?? 0;
    const tnrbRnrbPct = breakdown.tnrb_rnrb_percent ?? 0;
    // s.8L claim for the RNRB transfer (nil unless claimed — IHTA 1984 s.8G(3)); unanswered = not confirmed.
    const trnrbClaim = profile.iht_details?.predeceased_spouse?.trnrb_claim ?? null;
    // The NRB confidence governs only the NRB transfer; the RNRB transfer follows its claim (D06).
    const showNrbTransfer = !!tnrbConfidence && tnrbConfidence !== 'none' && tnrbNrbPct > 0;

    // ── RNRB sub-section content ─────────────────────────────
    const rnrbBeforeTaper = breakdown.rnrb_before_taper ?? 0;
    const taperAmount = breakdown.taper_amount ?? 0;
    const rnrbApplied = rnrb > 0 || rnrbBeforeTaper > 0;
    // The residence state is the shared Stage-1 resolution (never is_main_residence): an open nomination or an unknown
    // residence history is reported as such, never as "no main residence".
    const residence = resolveResidenceForConsumers(assets.properties, profile.iht_details);
    const mainHome = residence.qri;
    const hasDescendants =
        (profile.family?.children_over_18 ?? 0) > 0
        || (profile.family?.children_under_18 ?? 0) > 0;
    const residenceOpen = residence.nominationRequired || residence.residenceHistoryUnknown;
    // The RNRB did not exist before 6 Apr 2017: for an out-of-range death (pre-IHT or pre-2017) the selector raises
    // the date banner, and no residence state — nomination, unknown history, provisional residence — is described as
    // affecting an RNRB. Same gate as RnrbExplanation; the boundary is the engine's date-of-death blocker.
    const rnrbInForce = !selectReportPanels(ihtCalc).showBanner;
    const showRnrbBlock = rnrbInForce
        && (rnrbApplied || mainHome != null || residenceOpen || pendingItems.includes('MISSING_CLOSELY_INHERITED_RESIDENCE_VALUE'));

    let rnrbReason: string | null = null;
    if (!rnrbApplied) {
        if (rnrbBeforeTaper > 0 && taperAmount >= rnrbBeforeTaper) {
            rnrbReason = `The estate exceeds the £2,000,000 taper threshold; the full RNRB entitlement of ${fmtGBP(rnrbBeforeTaper)} has been tapered away.`;
        } else if (pendingItems.includes('MISSING_CLOSELY_INHERITED_RESIDENCE_VALUE')) {
            rnrbReason = 'No qualifying-residence details have yet been provided. The RNRB requires a main home passing to direct descendants.';
        } else if (residence.nominationRequired) {
            rnrbReason = 'More than one property may count as the residence, and a qualifying residence has not yet been nominated. Only one can count, and the executors or administrators choose which; until then the RNRB is withheld.';
        } else if (residence.residenceHistoryUnknown) {
            rnrbReason = 'It is not yet known whether they lived in each property as their home, so the residence for the RNRB cannot be identified yet.';
        } else if (mainHome) {
            if (mainHome.ownership_type === 'joint_tenants' && mainHome.joint_owner_relationship === 'spouse') {
                rnrbReason = 'The main residence passes to the surviving spouse by survivorship rather than to direct descendants. The unused RNRB may be transferable to the spouse\'s later estate.';
            } else {
                rnrbReason = 'The main residence does not appear to pass to direct descendants. The RNRB is only available where a qualifying home is inherited by lineal descendants.';
            }
        }
    }

    // ── Risks and red flags ──────────────────────────────────
    const redFlags = detectRedFlags(profile, assets, ihtCalc);
    const optimisations = detectOptimisations(profile, assets, ihtCalc);

    // ── Section numbering ────────────────────────────────────
    const numerals: string[] = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];
    let n = 0;
    const next = () => numerals[n++];
    const interpretationN = next(); // I
    const estateN = next();         // II
    const ihtN = next();            // III
    const pendingN = pendingItems.length > 0 ? next() : null;
    const risksN = redFlags.length > 0 ? next() : null;
    const planningN = optimisations.length > 0 ? next() : null;
    const nextStepsN = next();

    // ── Layout (printMode controls width and screen-only chrome) ──
    const containerCls = printMode
        ? 'max-w-[720px] mx-auto px-0 py-0 font-sans'
        : 'max-w-[760px] mx-auto px-6 sm:px-10 py-10 font-sans';

    return (
        <div className={containerCls}>
            <ReportMasthead
                caseRef={reportRef}
                preparedAt={preparedAtText}
                preparedFor={preparedFor}
            />

            <header className="mb-8">
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-3">
                    In the estate of
                </div>
                <h1 className="font-serif text-[34px] tracking-[-0.02em] text-[#1A1A1A] m-0 font-normal">
                    {deceasedName}
                </h1>
                <div className="text-[13px] text-[#6B6762] mt-1">
                    {[
                        d.date_of_death ? `Died ${dodLabel}` : null,
                        domicileLabel,
                        maritalLabel(d.maritalStatus),
                        grossEstate > 0 ? `gross estate ${fmtGBP(grossEstate)}` : null,
                    ].filter(Boolean).join(' · ')}
                </div>
            </header>

            {/* Cross-field conflicts: a blocking contradiction gates the confident
                headline so a "£0 / ready" claim can't read as settled while
                unresolved. The figures still appear below, framed as provisional. */}
            {consistencyBlocked ? (
                <div className="mb-8 space-y-4">
                    <ConsistencyConflictPanel findings={blockingFindings} goToStep={goToStep} />
                    <div data-testid="consistency-hold-state" className="bg-[#FAF3E0] border border-[#E8D89A] rounded px-5 py-4">
                        <p className="font-serif text-[16px] text-[#1A1A1A] m-0">
                            Some answers conflict — resolve these before relying on the result.
                        </p>
                        <p className="text-[12px] text-[#3F3D39] mt-1 m-0">
                            The figures below are provisional until the conflict above is resolved.
                        </p>
                    </div>
                </div>
            ) : (
                <div data-testid="report-headline">
                    <InterpretationLine>{interpretation}</InterpretationLine>

                    <VerdictTrio
                        probate={{
                            label: 'Probate route',
                            headline: probateInfo.statusLabel,
                            subtext: probateInfo.shortReason,
                        }}
                        filing={{
                            label: 'HMRC filing',
                            headline: filingForm,
                            subtext: filingSubtext,
                        }}
                        iht={{
                            label: trustEstateProvisional ? 'Inheritance tax (provisional)' : 'Inheritance tax',
                            headline: fmtGBP(ihtDue),
                            subtext: ihtSubtext,
                        }}
                    />
                </div>
            )}

            {/* Quiet pass-state — confirms the check ran (screen only). */}
            {consistencyEnabled && !consistencyBlocked && reviewFindings.length === 0 && !printMode && (
                <p data-testid="consistency-clean" className="text-[12px] text-[#6B6762] flex items-center gap-1.5 mb-6 no-print">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#6B6762]" /> No inconsistencies detected.
                </p>
            )}

            {/* Review nudges — inline, dismissible, non-gating. */}
            {consistencyEnabled && !consistencyBlocked && reviewFindings.length > 0 && (
                <div className="mb-6">
                    <ConsistencyReviewNudges findings={reviewFindings} goToStep={goToStep} />
                </div>
            )}

            {/* I — Our interpretation */}
            <section id="section-i" className="report-section mb-12">
                <SectionHeader numeral={interpretationN} title="Our interpretation" />
                <div className="font-serif text-[15px] leading-[1.7] text-[#1A1A1A]" style={{ maxWidth: 640 }}>
                    <p className="m-0 mb-4">
                        This report sets out the probate and inheritance tax position for the estate of{' '}
                        <strong>{deceasedName}</strong>
                        {d.date_of_death ? <>, who died on {dodLabel}</> : null}.
                        The deceased was {domicileLabel.toLowerCase()}
                        {maritalLabel(d.maritalStatus) ? <> and {maritalLabel(d.maritalStatus)}</> : null}.
                    </p>
                    <p className="m-0 mb-4">
                        Based on the assets and decisions recorded, the recommended filing route is{' '}
                        <strong>{filingForm}</strong>. {filingSubtext}
                    </p>
                    <p className="m-0">
                        {trustEstateProvisional
                            ? (ihtDue > 0
                                ? <>On the information currently entered, an inheritance tax liability of <strong>{fmtGBP(ihtDue)}</strong> is calculated at the {(ihtRate * 100).toFixed(0)}% rate. {TRUST_ESTATE_PROVISIONAL}</>
                                : <>{TRUST_ESTATE_ZERO_TAX} {TRUST_ESTATE_PROVISIONAL}</>)
                            : ihtDue > 0
                            ? <>An inheritance tax liability of <strong>{fmtGBP(ihtDue)}</strong> arises at the {(ihtRate * 100).toFixed(0)}% rate. {ihtSubtext}</>
                            : <>No inheritance tax is expected on the figures provided. {breakdown.zero_tax_explanation ?? ''}</>
                        }
                    </p>
                </div>
            </section>

            {/* II — Estate position */}
            <section id="section-ii" className="report-section mb-12">
                <SectionHeader
                    numeral={estateN}
                    title="Estate position"
                    subtitle="Schedule of assets, debts and net estate value."
                />
                {ledger.length > 0 ? (
                    ledger.map(row => (
                        <LedgerRow
                            key={row.id}
                            label={row.note
                                ? <span>{row.label} <span className="text-[#6B6762] text-[11px]">— {row.note}</span></span>
                                : row.label}
                            value={fmtGBP(row.value)}
                        />
                    ))
                ) : (
                    <div className="text-[13px] text-[#6B6762] italic py-4">
                        No itemised assets recorded.
                    </div>
                )}
                <LedgerRow
                    label="Gross estate"
                    value={fmtGBP(grossEstate)}
                    emphasis="subtotal"
                />
                {debts > 0 && (
                    <LedgerRow
                        label="Less: debts and liabilities"
                        value={`(${fmtGBP(debts)})`}
                        muted
                    />
                )}
                <LedgerRow
                    label="Net estate"
                    value={fmtGBP(netEstate)}
                    emphasis="total"
                />

                {assets.foreign_assets?.exists === true && (
                    <SubSection title="Assets outside the UK">
                        <p className="m-0">
                            The estate includes assets located outside the UK
                            {(assets.foreign_assets.countries ?? []).length > 0
                                ? ` in ${assets.foreign_assets.countries!.join(', ')}`
                                : ''}.
                            Separate probate or succession procedures may be required in
                            the relevant jurisdiction, and foreign inheritance taxes may
                            apply alongside UK IHT.
                        </p>
                    </SubSection>
                )}
            </section>

            {/* III — IHT calculation */}
            <section id="section-iii" className="report-section mb-12">
                <SectionHeader
                    numeral={ihtN}
                    title="Inheritance tax calculation"
                    subtitle={`Rate applied: ${(ihtRate * 100).toFixed(0)}%${breakdown.tax_year ? ` · tax year ${breakdown.tax_year}` : ''}.`}
                />
                <LedgerRow label="Net estate" value={fmtGBP(netEstate)} />
                {spouseExemption > 0 && (
                    <LedgerRow
                        label="Less: spouse / civil partner exemption"
                        value={`(${fmtGBP(spouseExemption)})`}
                        muted
                    />
                )}
                {charityExemption > 0 && (
                    <LedgerRow
                        label={breakdown.charitable_rate_applies
                            ? 'Less: charitable exemption (qualifies for 36% reduced rate)'
                            : 'Less: charitable exemption'}
                        value={`(${fmtGBP(charityExemption)})`}
                        muted
                    />
                )}
                {bprRelief > 0 && (
                    <LedgerRow label="Less: Business Property Relief" value={`(${fmtGBP(bprRelief)})`} muted />
                )}
                {aprRelief > 0 && (
                    <LedgerRow label="Less: Agricultural Property Relief" value={`(${fmtGBP(aprRelief)})`} muted />
                )}
                {giftsChargeable > 0 && (
                    <LedgerRow
                        label="Add: chargeable lifetime gifts (within 7 years)"
                        value={fmtGBP(giftsChargeable)}
                        muted
                    />
                )}
                {nrb > 0 && (
                    <LedgerRow label="Less: Nil Rate Band" value={`(${fmtGBP(nrb)})`} muted />
                )}
                {rnrb > 0 && (
                    <LedgerRow label="Less: Residence Nil Rate Band" value={`(${fmtGBP(rnrb)})`} muted />
                )}
                {totalAllowance > 0 && nrb === 0 && rnrb === 0 && (
                    <LedgerRow label="Less: total allowances applied" value={`(${fmtGBP(totalAllowance)})`} muted />
                )}
                <LedgerRow label="Taxable estate" value={fmtGBP(taxableValue)} emphasis="subtotal" />
                <LedgerRow
                    label={`Inheritance tax at ${(ihtRate * 100).toFixed(0)}%${trustEstateProvisional ? ' (provisional)' : ''}`}
                    value={fmtGBP(ihtDue)}
                    emphasis="total"
                />

                {/* Sub: Residence Nil Rate Band */}
                {showRnrbBlock && (
                    <SubSection title="Residence Nil Rate Band">
                        {rnrbApplied ? (
                            <>
                                <p className="m-0 mb-2">
                                    {fmtGBP(rnrb)} of RNRB is applied
                                    {rnrbBeforeTaper > rnrb && taperAmount > 0
                                        ? <> after a £{taperAmount.toLocaleString('en-GB')} taper reduction (estate exceeds £2,000,000).</>
                                        : '.'}
                                    {downsizing > 0
                                        ? <> Includes a downsizing addition of {fmtGBP(downsizing)}.</>
                                        : null}
                                </p>
                                <p className="m-0 text-[#6B6762] text-[12px]">
                                    The RNRB provides up to £175,000 additional allowance when a
                                    main residence passes to direct descendants (IHTA 1984 ss 8D–8M).
                                </p>
                            </>
                        ) : (
                            <p className="m-0">{rnrbReason}</p>
                        )}
                    </SubSection>
                )}

                {/* Sub: Transferable Nil Rate Band */}
                {(showNrbTransfer || tnrbRnrbPct > 0) && (
                    <SubSection title="Transferable allowances">
                        <p className="m-0">
                            A transfer of unused allowance from a predeceased spouse or civil
                            partner has been recorded — {showNrbTransfer ? <>NRB <strong>{tnrbNrbPct}%</strong></> : null}
                            {showNrbTransfer && tnrbRnrbPct > 0 ? ' · ' : null}
                            {tnrbRnrbPct > 0 ? <>RNRB <strong>{tnrbRnrbPct}%</strong></> : null}.
                            {showNrbTransfer && tnrbConfidence === 'possible'
                                ? ' The NRB percentage has not been independently verified — confirm before filing IHT402.'
                                : ' Form IHT402 (NRB) or IHT436 (RNRB) will be required to claim it.'}
                            {tnrbRnrbPct > 0 && (trnrbClaim === 'yes'
                                ? ' A claim for the RNRB transfer is being made; the percentage is as entered and has not been independently verified.'
                                : trnrbClaim === 'no'
                                    ? ' No claim for the RNRB transfer is being made, so it is not included in this calculation.'
                                    : ' Whether the RNRB transfer is being claimed has not been confirmed, so it is not included in this calculation.')}
                        </p>
                    </SubSection>
                )}

                {/* Sub: Lifetime gifts within 7 years */}
                {giftTaperDetails.length > 0 && (
                    <SubSection title="Lifetime gifts within 7 years">
                        <p className="m-0 mb-3 text-[#3F3D39]">
                            {giftTaperDetails.length} gift{giftTaperDetails.length === 1 ? '' : 's'} recorded.
                            {taperReliefTotal > 0 && <> Taper relief of {fmtGBP(taperReliefTotal)} has reduced the gift tax.</>}
                        </p>
                        <div className="border-t border-[#E8E4DB]">
                            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 py-2 border-b border-[#E8E4DB] text-[10px] tracking-[0.16em] uppercase text-[#6B6762]">
                                <span>Gift date</span>
                                <span className="text-right">Years before</span>
                                <span className="text-right">Taper</span>
                                <span className="text-right">Tax after taper</span>
                            </div>
                            {giftTaperDetails.map((g, i) => (
                                <div
                                    key={i}
                                    className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 py-2 border-b border-dotted border-[#E8E4DB] text-[13px]"
                                >
                                    <span className="text-[#1A1A1A]">{fmtDate(g.date)}</span>
                                    <span className="text-right text-[#3F3D39] font-mono tabular-nums">{formatYears(g.yearsBefore)}</span>
                                    <span className="text-right text-[#3F3D39] font-mono tabular-nums">{Math.round(g.taperPercent * 100)}%</span>
                                    <span className="text-right text-[#1A1A1A] font-mono tabular-nums">{fmtGBP(g.taxAfterTaper)}</span>
                                </div>
                            ))}
                        </div>
                    </SubSection>
                )}

                {/* Sub: Gift exemption cascade (s21 → annual exemption), top to
                      bottom so an IHT403 reader can follow it. Renders when an s21
                      assessment ran or any annual exemption was applied. */}
                {(() => {
                    const s21Exempt = breakdown.gifts_income_exempt_total ?? 0;
                    const s21Overflow = breakdown.gifts_income_overflow_total ?? 0;
                    const aeApplied = breakdown.gifts_ae_applied_total ?? 0;
                    const s21Flags = (ihtCalc.confidence?.flags ?? []).filter(f => f.field === 'gift_income_exemption');
                    const s21Ran = s21Exempt > 0 || s21Overflow > 0 || s21Flags.length > 0;
                    if (!(s21Ran || aeApplied > 0)) return null;
                    const assessed = s21Exempt + s21Overflow;
                    return (
                        <SubSection title="Gift exemptions">
                            <div className="border-t border-[#E8E4DB]">
                                {s21Ran && (
                                    <>
                                        <LedgerRow label="Regular gifts assessed (s21)" value={fmtGBP(assessed)} />
                                        <LedgerRow label="— Exempt as normal expenditure out of income" value={fmtGBP(s21Exempt)} muted />
                                        {s21Overflow > 0 && (
                                            <LedgerRow label="— Reverted to potentially exempt transfer treatment" value={fmtGBP(s21Overflow)} muted />
                                        )}
                                    </>
                                )}
                                {aeApplied > 0 && (
                                    <LedgerRow label="Annual exemption applied (£3,000 / year)" value={fmtGBP(aeApplied)} muted />
                                )}
                            </div>
                            {aeApplied > 0 && (
                                <p className="m-0 mt-2 text-[#6B6762] text-[12px]">
                                    The annual exemption is applied automatically from the gift dates and amounts — there is no figure to enter. It covers the overflow above and any one-off gifts, but never the s21-exempt portion: a gift fully exempt as normal expenditure out of income consumes no annual exemption.
                                </p>
                            )}
                            {s21Ran && (
                                <p className="m-0 mt-2 text-[#6B6762] text-[12px]">
                                    These figures depend on the income and expenditure records for each year; the burden of proof for the s21 exemption rests with the personal representatives. This breakdown maps to IHT403 Schedule 2.
                                </p>
                            )}
                            {s21Flags.length > 0 && (
                                <ul className="list-none p-0 m-0 mt-3 space-y-1.5">
                                    {s21Flags.map((f, i) => (
                                        <li key={i} className="flex items-start gap-2 text-[12px] leading-relaxed">
                                            <span className={
                                                f.level === 'missing' ? 'text-[#1A1A1A]'
                                                    : f.level === 'assumed' ? 'text-[#7A5A00]'
                                                        : 'text-[#6B6762]'
                                            }>{f.level === 'missing' ? '✕' : '⚠'}</span>
                                            <span className="text-[#3F3D39]">{f.note}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </SubSection>
                    );
                })()}

                {/* Sub: Property rationale (joint tenancy / TIC mix) */}
                {(assets.properties ?? []).length > 0 && (() => {
                    const jt = (assets.properties ?? []).filter(p => p.ownership_type === 'joint_tenants');
                    const tic = (assets.properties ?? []).filter(p => p.ownership_type === 'tenants_in_common');
                    const sole = (assets.properties ?? []).filter(p => p.ownership_type === 'sole');
                    if (jt.length === 0 && tic.length === 0) return null;
                    return (
                        <SubSection title="Property — how each share passes">
                            <ul className="list-none p-0 m-0 space-y-1">
                                {sole.map((p, i) => (
                                    <li key={`s-${i}`} className="m-0">
                                        <strong>{p.label || 'Sole property'}</strong> — held in the sole name of the deceased; passes under the will / intestacy and requires probate.
                                    </li>
                                ))}
                                {tic.map((p, i) => (
                                    <li key={`t-${i}`} className="m-0">
                                        <strong>{p.label || 'TIC property'}</strong> — held as tenants in common ({p.deceased_share_percent ?? '?'}% share); the deceased's share passes under the will and requires probate.
                                    </li>
                                ))}
                                {jt.map((p, i) => (
                                    <li key={`j-${i}`} className="m-0">
                                        <strong>{p.label || 'JT property'}</strong> — held as joint tenants; passes by survivorship outside the estate. The value is included for IHT but no probate-grant action is needed for this share.
                                    </li>
                                ))}
                            </ul>
                        </SubSection>
                    );
                })()}
            </section>

            {/* IV — Outstanding items (conditional) */}
            {pendingN && (
                <section id="section-iv" className="report-section mb-12">
                    <SectionHeader
                        numeral={pendingN}
                        title="Outstanding items before filing"
                        subtitle={`${pendingItems.length} item${pendingItems.length === 1 ? '' : 's'} requiring executor action before submission.`}
                    />
                    <ol className="list-none p-0 m-0">
                        {pendingItems.map((key, i) => (
                            <li
                                key={key}
                                className="flex gap-4 py-3 border-b border-dotted border-[#E8E4DB] last:border-b-0"
                            >
                                <span className="font-serif italic text-[#3730A3] text-[14px] w-6 shrink-0">
                                    {i + 1}.
                                </span>
                                <span className="text-[13px] text-[#1A1A1A] leading-relaxed">
                                    {pendingLabel(key)}
                                </span>
                            </li>
                        ))}
                    </ol>
                </section>
            )}

            {/* V — Risks and red flags (conditional) */}
            {risksN && (
                <section id="section-risks" className="report-section mb-12">
                    <SectionHeader
                        numeral={risksN}
                        title="Risks and red flags"
                        subtitle="Issues identified that may affect the calculation, the filing route or the timeline."
                    />
                    <ol className="list-none p-0 m-0">
                        {redFlags.map((f, i) => (
                            <li key={f.id} className="py-4 border-b border-[#E8E4DB] last:border-b-0">
                                <h3 className="font-serif text-[16px] font-medium tracking-[-0.005em] text-[#1A1A1A] m-0 mb-1">
                                    {`${i + 1}. ${f.title}`}
                                </h3>
                                <p className="text-[13px] leading-relaxed text-[#3F3D39] m-0" style={{ maxWidth: 640 }}>
                                    {f.detail}
                                </p>
                            </li>
                        ))}
                    </ol>
                </section>
            )}

            {/* VI — Planning opportunities (conditional, only if tax is due) */}
            {planningN && (
                <section id="section-planning" className="report-section mb-12">
                    <SectionHeader
                        numeral={planningN}
                        title="Planning opportunities"
                        subtitle="Reliefs and exemptions worth reviewing before filing — not advice."
                    />
                    <ol className="list-none p-0 m-0">
                        {optimisations.map((o, i) => (
                            <li key={o.id} className="py-4 border-b border-[#E8E4DB] last:border-b-0">
                                <h3 className="font-serif text-[16px] font-medium tracking-[-0.005em] text-[#1A1A1A] m-0 mb-1">
                                    {`${i + 1}. ${o.title}`}
                                </h3>
                                <p className="text-[13px] leading-relaxed text-[#3F3D39] m-0" style={{ maxWidth: 640 }}>
                                    {o.detail}
                                </p>
                            </li>
                        ))}
                    </ol>
                </section>
            )}

            {/* Final — Next steps & references */}
            <section id="section-next-steps" className="report-section mb-12">
                <SectionHeader
                    numeral={nextStepsN}
                    title="Next steps &amp; statutory references"
                />
                {actionPack.actions.length > 0 ? (
                    <ol className="list-none p-0 m-0">
                        {actionPack.actions.map((action, i) => (
                            <li
                                key={action.id || i}
                                className="flex gap-4 py-4 border-b border-[#E8E4DB] last:border-b-0"
                            >
                                <span className="font-serif italic text-[#3730A3] text-[16px] w-7 shrink-0 leading-tight">
                                    {i + 1}.
                                </span>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-serif text-[16px] font-medium tracking-[-0.005em] text-[#1A1A1A] m-0 mb-1">
                                        {action.title}
                                    </h3>
                                    <p className="text-[13px] leading-relaxed text-[#3F3D39] m-0">
                                        {action.description}
                                    </p>
                                    {action.links && action.links.length > 0 && (
                                        <ul className="list-none p-0 mt-2 space-y-0.5">
                                            {action.links.map((l, j) => (
                                                <li key={j} className="text-[11px] text-[#3730A3]">
                                                    <a href={l.url}>{l.label || l.url}</a>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ol>
                ) : (
                    <p className="text-[13px] text-[#6B6762] italic">
                        No outstanding actions recorded.
                    </p>
                )}
            </section>

            <footer className="report-footer mt-12 pt-6 border-t border-[#E8E4DB]">
                <p className="text-[10px] text-[#AFA99F] italic m-0">
                    This report is an estimate based on the information recorded in
                    Estateclarity. Actual liability may vary once full asset values,
                    exemptions and reliefs are confirmed.
                </p>
            </footer>
        </div>
    );
};

// ── Small helper for the inline sub-sections under main sections ──
const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="mt-8 pl-4 border-l border-[#E8E4DB]" style={{ maxWidth: 640 }}>
        <h3 className="font-serif text-[16px] font-medium tracking-[-0.005em] text-[#1A1A1A] m-0 mb-2">
            {title}
        </h3>
        <div className="text-[13px] leading-relaxed text-[#3F3D39]">
            {children}
        </div>
    </div>
);
